import { SELECTED_TIMEZONE_LOCALSTORAGE_KEY } from '@/constant/global/global.constant';
import moment from 'moment-timezone';

export enum EAppTimezones {
  UTC = 'UTC',
  EST = 'America/New_York',
  CST = 'America/Chicago',
  MST = 'America/Denver',
  PST = 'America/Los_Angeles',
  IST = 'Asia/Kolkata',
  CHINA_CST = 'Asia/Shanghai',
}

/**
 * Formats a given time string to the selected timezone.
 *
 * @param timezone - Selected timezone from EAppTimezones enum.
 * @param timeString - Time string to be formatted (optional).
 * @returns Formatted time string or a hyphen '-' if time is not provided or invalid.
 */
export function formatTimeByTimezone(
  timezone: EAppTimezones = EAppTimezones.EST,
  timeString?: string,
): string {
  if (!timeString || !moment(timeString, moment.ISO_8601, true).isValid()) {
    return '-';
  }

  // return moment.tz(timeString, timezone).format("lll z")
  return moment.tz(timeString, timezone).format('MMM DD, YYYY h:mm:ss A z');
}

/**
 * Formats a given time string to the selected timezone.
 *
 * @param timezone - Selected timezone from EAppTimezones enum.
 * @param timeString - Time string to be formatted (optional).
 * @returns Formatted time string or a hyphen '-' if time is not provided or invalid.
 */
export function formatTimeAgoByTimezone(
  timezone: EAppTimezones = EAppTimezones.EST,
  timeString?: string,
): string {
  if (!timeString || !moment(timeString, moment.ISO_8601, true).isValid()) {
    return '-';
  }

  // return moment.tz(timeString, timezone).format("lll z")
  return moment.tz(timeString, timezone).fromNow();
}

/**
 * Formats a date to the selected timezone or returns '-' if date is not valid or not provided.
 *
 * @param date - Date string or Date object to be formatted.
 * @returns Formatted date string or a hyphen '-' if date is invalid or not provided.
 */
export const getDateTime = (date?: string | Date): string => {
  const storedSelectedTimeZone = localStorage.getItem(
    SELECTED_TIMEZONE_LOCALSTORAGE_KEY,
  ) as EAppTimezones;

  // Ensure the stored timezone is a valid EAppTimezones value; fallback to EST if not.
  const selectedTimeZone = Object.values(EAppTimezones).includes(
    storedSelectedTimeZone,
  )
    ? storedSelectedTimeZone
    : EAppTimezones.EST;

  if (!date || !moment(date, moment.ISO_8601, true).isValid()) {
    return '-';
  }

  return formatTimeByTimezone(selectedTimeZone, date.toString());
};

/**
 * Formats a date to the selected timezone or returns '-' if date is not valid or not provided.
 *
 * @param date - Date string or Date object to be formatted.
 * @returns Formatted date string or a hyphen '-' if date is invalid or not provided.
 */
export const getDateTimeAgo = (date?: string | Date): string => {
  const storedSelectedTimeZone = localStorage.getItem(
    SELECTED_TIMEZONE_LOCALSTORAGE_KEY,
  ) as EAppTimezones;

  // Ensure the stored timezone is a valid EAppTimezones value; fallback to EST if not.
  const selectedTimeZone = Object.values(EAppTimezones).includes(
    storedSelectedTimeZone,
  )
    ? storedSelectedTimeZone
    : EAppTimezones.EST;

  if (!date || !moment(date, moment.ISO_8601, true).isValid()) {
    return '-';
  }

  return formatTimeAgoByTimezone(selectedTimeZone, date.toString());
};

type IProcessedTimeProps = Date | string;

export const getProcessedTime = (
  startDate: IProcessedTimeProps,
  endDate: IProcessedTimeProps,
) => {
  // Calculate the duration between createdOn and processedOn
  const duration = moment.duration(moment(endDate).diff(moment(startDate)));

  // Extract days, hours, minutes, and seconds
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  // Format the duration
  const formattedDuration = [
    days > 0 ? `${days}d` : '',
    hours > 0 ? `${hours}h` : '',
    minutes > 0 ? `${minutes}m` : '',
    seconds > 0 ? `${seconds}s` : '',
  ]
    .filter(Boolean)
    .join(' ');
  return formattedDuration;
};

export const getSelectedTimeZone = () => {
  const timezonesDropdownList = Object.entries(EAppTimezones).map(
    ([key, value], index) => ({
      id: (index + 1).toString(),
      label: key,
      value,
    }),
  );

  const storedSelectedTimeZone =
    localStorage.getItem(SELECTED_TIMEZONE_LOCALSTORAGE_KEY) ||
    EAppTimezones.EST;

  const initialTimeZone =
    timezonesDropdownList.find((tz) => tz.value === storedSelectedTimeZone) ||
    timezonesDropdownList[1];
  return initialTimeZone.value;
};

export const applyTimeZoneToSelectedCustomDate = (
  date: string,
  format: string,
  selectedTimeZone: string,
  start: boolean,
) => {
  return start
    ? moment.tz(date, format, selectedTimeZone).startOf('day').format()
    : moment.tz(date, format, selectedTimeZone).endOf('day').format();
};

export const getTimezoneAbbreviation = (
  timeZoneValue: string,
  timestamp?: number | string,
): string => {
  // If no timestamp is provided, use the current time
  const time = timestamp ? moment(timestamp) : moment();
  // .tz(zone).format('z') returns the specific abbreviation (EST, EDT, CST, CDT, etc.)
  // for that exact point in time in that specific geographic region.
  return moment.tz(time, timeZoneValue).format('z');
};

/**
 * Returns the default date range for the dashboard (Last 7 Days)
 * aligned to the start of the first day and end of today.
 */
export const getDefaultDashboardRange = (timezone: string) => {
  return {
    startDate: moment()
      .subtract(6, 'days')
      .tz(timezone)
      .startOf('day')
      .format(),
    endDate: moment().tz(timezone).endOf('day').format(),
  };
};

/**
 * Normalise the `range` field of each metric row to the canonical format
 * expected by chart formatters, based on the requested groupBy.
 *
 *   day   → kept as-is  ("2024-07-01")
 *   week  → canonicalise separator to " - "
 *           API may return any of: " to ", " – ", " — ", " - "
 *           all normalised to "YYYY-MM-DD - YYYY-MM-DD"
 *   month → truncate to "YYYY-MM"   (API sometimes returns "YYYY-MM-DD")
 *   year  → truncate to "YYYY"      (API sometimes returns "YYYY-MM" or "YYYY-MM-DD")
 *
 * Generic — works for both VDI metric rows and Alert metric rows (or any object
 * that carries a `range` string field).
 */
export function normalizeMetricRanges<T extends { range?: string }>(
  items: T[],
  groupBy: 'day' | 'week' | 'month' | 'year',
): T[] {
  if (groupBy === 'day') return items;
  return items.map((item) => {
    const range = item.range;
    if (!range) return item;

    if (groupBy === 'week') {
      // Accept any word separator between two YYYY-MM-DD dates and normalise to " - "
      const weekMatch = range.match(
        /^(\d{4}-\d{2}-\d{2})\s+(?:to|–|-|—)\s+(\d{4}-\d{2}-\d{2})$/,
      );
      if (weekMatch) {
        const canonical = `${weekMatch[1]} - ${weekMatch[2]}`;
        return range === canonical ? item : { ...item, range: canonical };
      }
      return item;
    }

    if (groupBy === 'month' && /^\d{4}-\d{2}-\d{2}/.test(range)) {
      return { ...item, range: range.slice(0, 7) }; // "2024-01-01" → "2024-01"
    }
    if (groupBy === 'year' && /^\d{4}-\d{2}/.test(range)) {
      return { ...item, range: range.slice(0, 4) }; // "2024-01" or "2024-01-01" → "2024"
    }
    return item;
  });
}

/**
 * Returns a compact, human-readable date-range string based on the span of the range.
 *
 * Examples:
 *   ≤ 31 days  → "Jun 27 – Jul 3, 2026"     (same year: "Jun 27 – Jul 3")
 *   ≤ 91 days  → "Jun 2026 – Aug 2026"
 *   ≤ 365 days → "Jan – Dec 2025"            (same year)
 *               or "Oct 2024 – Feb 2025"     (cross-year)
 *   > 365 days → "2023 – 2026"
 */
export const formatDateRangeSummary = (
  startDate: string | Date | undefined | null,
  endDate: string | Date | undefined | null,
  timezone?: string,
): string => {
  if (!startDate || !endDate) return '';
  const tz = timezone || getSelectedTimeZone();
  const s = moment(startDate).tz(tz);
  const e = moment(endDate).tz(tz);
  if (!s.isValid() || !e.isValid()) return '';

  const days = e.diff(s, 'days');

  if (days <= 31) {
    // Day range: "Jun 27 – Jul 3, 2026"
    if (s.year() === e.year()) {
      if (s.month() === e.month()) {
        return `${s.format('MMM D')} – ${e.format('D, YYYY')}`;
      }
      return `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`;
    }
    return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
  }

  if (days <= 91) {
    // Week/month span: "Jun – Aug 2026"
    if (s.year() === e.year()) {
      if (s.month() === e.month()) return s.format('MMMM YYYY');
      return `${s.format('MMM')} – ${e.format('MMM YYYY')}`;
    }
    return `${s.format('MMM YYYY')} – ${e.format('MMM YYYY')}`;
  }

  if (days <= 365) {
    // Month range: "Jan – Dec 2025"
    if (s.year() === e.year()) {
      if (s.month() === e.month()) return s.format('MMMM YYYY');
      return `${s.format('MMM')} – ${e.format('MMM YYYY')}`;
    }
    return `${s.format('MMM YYYY')} – ${e.format('MMM YYYY')}`;
  }

  // Year range: "2023 – 2026"
  if (s.year() === e.year()) return String(s.year());
  return `${s.format('YYYY')} – ${e.format('YYYY')}`;
};
